/**
 * Video Activation API service.
 *
 * Provides functions for managing video activation jobs,
 * speaker confirmation, and wisdom moment retrieval.
 */

import { axiosInstance } from './api';

const BASE = '/videoactivation';

/**
 * Create a new video activation job.
 * @param {Object} data - { title: string, description?: string, video_ids: string[] }
 * @returns {Object} { id, title, status, total_videos }
 */
export const createVideoActivationJob = async (data) => {
  const response = await axiosInstance.post(`${BASE}/jobs/`, data);
  return response.data;
};

/**
 * Get the status and progress of a job.
 * @param {string} jobId - UUID of the job
 * @returns {Object} Job status with records array
 */
export const getJobStatus = async (jobId) => {
  const response = await axiosInstance.get(`${BASE}/jobs/${jobId}/status/`);
  return response.data;
};

/**
 * Get speakers detected in transcribed videos for confirmation.
 * @param {string} jobId - UUID of the job
 * @returns {Object} { job_id, title, videos: [...], family_members: [...] }
 */
export const getJobSpeakers = async (jobId) => {
  const response = await axiosInstance.get(`${BASE}/jobs/${jobId}/speakers/`);
  return response.data;
};

/**
 * Confirm speaker identities for a video.
 * @param {string} jobId - UUID of the job
 * @param {Object} data - { record_id: string, confirmations: [{ speaker_label, person_id }] }
 * @returns {Object} { record_id, status }
 */
export const confirmSpeakers = async (jobId, data) => {
  const response = await axiosInstance.post(`${BASE}/jobs/${jobId}/confirm/`, data);
  return response.data;
};

/**
 * Finalize speaker confirmations and trigger wisdom moment extraction.
 * @param {string} jobId - UUID of the job
 * @returns {Object} { job_id, status, message }
 */
export const completeJob = async (jobId) => {
  const response = await axiosInstance.post(`${BASE}/jobs/${jobId}/complete/`);
  return response.data;
};

/**
 * Get all extracted wisdom moments for a job.
 * @param {string} jobId - UUID of the job
 * @returns {Object} { job_id, title, status, moments: [...], total_moments }
 */
export const getJobMoments = async (jobId) => {
  const response = await axiosInstance.get(`${BASE}/jobs/${jobId}/moments/`);
  return response.data;
};

/**
 * Get wisdom moments for the current family.
 * @param {Object} [params] - { video_id?, speaker_id?, category?, q? }
 * @returns {Object} { moments, stats, speakers, result_count }
 */
export const getWisdomMoments = async (params = {}) => {
  const searchParams = new URLSearchParams();
  if (params.video_id) searchParams.set('video_id', params.video_id);
  if (params.speaker_id) searchParams.set('speaker_id', params.speaker_id);
  if (params.category) searchParams.set('category', params.category);
  if (params.person_id) searchParams.set('person_id', params.person_id);
  if (params.q) searchParams.set('q', params.q);
  const query = searchParams.toString();
  const response = await axiosInstance.get(`${BASE}/moments/${query ? `?${query}` : ''}`);
  return response.data;
};

/**
 * Update a wisdom moment's visibility (speaker-only).
 * @param {string} momentId
 * @param {Object} data - { visibility?: 'private'|'close'|'family', status?: 'confirmed'|'dismissed' }
 */
export const updateMomentVisibility = async (momentId, data) => {
  const response = await axiosInstance.patch(`${BASE}/moments/${momentId}/visibility/`, data);
  return response.data;
};

export const tagPersonInMoment = async (momentId, personId) => {
  const response = await axiosInstance.post(`${BASE}/moments/${momentId}/tag/`, { person_id: personId });
  return response.data;
};

export const untagPersonInMoment = async (momentId, personId) => {
  const response = await axiosInstance.post(`${BASE}/moments/${momentId}/untag/`, { person_id: personId });
  return response.data;
};

export const searchPeople = async (q = '', limit = 20) => {
  const params = new URLSearchParams();
  if (q) params.set('q', q);
  params.set('limit', String(limit));
  const response = await axiosInstance.get(`${BASE}/people/search/?${params.toString()}`);
  return response.data;
};

/**
 * Get count of speakers awaiting confirmation.
 * @returns {Object} { count: number, job_ids: string[] }
 */
export const getPendingConfirmations = async () => {
  const response = await axiosInstance.get(`${BASE}/pending-confirmations/`);
  return response.data;
};

/**
 * Resolve a single speaker (convenience wrapper).
 * @param {string} jobId
 * @param {string} recordId - UUID of the processing record
 * @param {string} speakerLabel
 * @param {string} resolution - 'confirmed' | 'dismissed' | 'deferred'
 * @param {string} [personId] - required when resolution='confirmed'
 */
export const resolveSpeaker = async (jobId, recordId, speakerLabel, resolution, personId, guestName) => {
  const payload = {
    record_id: recordId,
    resolutions: [{
      speaker_label: speakerLabel,
      resolution,
      ...(personId ? { person_id: personId } : {}),
      ...(guestName ? { guest_name: guestName } : {}),
    }],
  };
  return confirmSpeakers(jobId, payload);
};

/**
 * @deprecated Use resolveSpeaker instead. Kept for backward compatibility.
 */
export const confirmSpeaker = async (jobId, speakerLabel, action, personId) => {
  const speakerData = await getJobSpeakers(jobId);
  const video = speakerData.videos?.find(v =>
    v.speakers?.some(s => s.speaker_label === speakerLabel)
  );
  if (!video) return;

  const resolution = action === 'outside_family' ? 'dismissed'
    : action === 'skip' ? 'deferred'
    : 'confirmed';

  return resolveSpeaker(jobId, video.record_id, speakerLabel, resolution, personId);
};

/**
 * Alias used by SpeakerConfirmationInline spec.
 */
export const getSpeakersForConfirmation = getJobSpeakers;

/**
 * Alias used by spec.
 */
export const completeConfirmation = completeJob;

/**
 * Get processing status for one or more videos by MediaFile UUID.
 * @param {string[]} mediaFileIds - Array of MediaFile UUIDs
 * @returns {Object} Map of mediaFileId -> { status, status_display, wisdom_moments_count, ... }
 */
export const getVideoProcessingStatus = async (mediaFileIds) => {
  if (!mediaFileIds || mediaFileIds.length === 0) return {};
  const ids = mediaFileIds.join(',');
  const response = await axiosInstance.get(`${BASE}/video-status/?media_file_ids=${ids}`);
  return response.data;
};

/**
 * Get transcript data for a video.
 * @param {string} mediaFileId - MediaFile UUID
 * @returns {Object} { status, duration, speakers, cues: [{ start, end, spk, text }] }
 */
export const getVideoTranscript = async (mediaFileId) => {
  const response = await axiosInstance.get(`${BASE}/transcript/${mediaFileId}/`);
  return response.data;
};

/**
 * Re-trigger transcription for a video.
 * @param {string} mediaFileId - MediaFile UUID
 */
export const retriggerTranscription = async (mediaFileId) => {
  const response = await axiosInstance.post(`${BASE}/transcript/${mediaFileId}/retry/`);
  return response.data;
};
